import React from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

interface SizeMeasurementsTableProps {
  valueList: string[][];
  fixedColumnWidth: string;
  columnMinWidth?: string;
  handleSetColRef: (cell: string, ref: HTMLSpanElement | null) => void;
}

export const SizeMeasurementsTable = ({
  valueList,
  fixedColumnWidth,
  columnMinWidth = '84px',
  handleSetColRef,
}: SizeMeasurementsTableProps) => {
  const [header, ...body] = valueList;

  return (
    <TableContainer>
      <Table>
        <thead>
          <tr>
            {header.map((value, index) => (
              <Th key={index} width={index === 0 ? fixedColumnWidth : columnMinWidth} sticky={index === 0}>
                <LineClamp ref={(ref) => handleSetColRef(value, ref)}>{value}</LineClamp>
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, rowIndex) => (
            <Tr key={rowIndex} border>
              {row.map((cell, cellIndex) => (
                <Td
                  key={cellIndex}
                  width={cellIndex === 0 ? fixedColumnWidth : columnMinWidth}
                  sticky={cellIndex === 0}
                >
                  <CellText>{cell}</CellText>
                </Td>
              ))}
            </Tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  );
};

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  tbody {
    tr:last-child {
      border-bottom: 1px solid ${vars.color.fillPrimary};
    }
  }
`;

const Th = styled.th<{ width: string; sticky?: boolean }>`
  padding: 12px 16px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: ${vars.color.textPrimary};
  background-color: ${vars.color.fillTertiary};
  min-width: ${({ width }) => width};
  max-width: ${({ width }) => width};
  ${({ sticky }) => sticky && `position: sticky; left: 0; text-align: left;`}
`;

const Tr = styled.tr<{ border: boolean }>`
  height: 37px;
  ${({ border }) => border && `border-bottom: 1px solid ${vars.color.fillTertiary};`}
`;

const Td = styled.td<{ width: string; sticky?: boolean }>`
  padding: 0;
  text-align: center;
  font-size: 11px;
  font-weight: 400;
  color: ${vars.color.textSecondary};
  min-width: ${({ width }) => width};
  max-width: ${({ width }) => width};
  position: sticky;
  ${({ sticky }) =>
    sticky && `left: 0; z-index: 1; text-align: left; font-weight: 500; color: ${vars.color.textPrimary};`}
`;

const LineClamp = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;
const CellText = styled.div`
  padding: 12px 16px;
  display: block;
  width: 100%;
  word-break: break-all;
  background-color: ${vars.color.staticWhite};
`;
